import ModalComponent from '@/Components/Modal/Modal';
import { Button, Flex, Form, Input } from 'antd';
import { Controller, useForm } from 'react-hook-form';
import { ModuleForm } from '../types';
import { useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import validation from '../validation';
import useCreateModule from '@/Apis/hooks/mutations/useCreateModule/useCreateModule';
import useDetailModule from '@/Apis/hooks/queries/useModuleById/useModuleById';
import useUpdateModule from '@/Apis/hooks/mutations/useUpdateModule/useUpdateModule';

interface Props {
	id: number;
	isEditMode: boolean;
	onClose: () => void;
	refetch: () => void;
}

const AddModal: React.FC<Props> = ({ onClose, isEditMode, refetch, id }) => {
	const { control, handleSubmit, reset } = useForm<ModuleForm>({
		defaultValues: {
			id: 0,
			name: '',
			shortName: '',
			remark: '',
		},
		resolver: yupResolver(validation),
	});

	const {
		data: { data: moduleDetail },
	} = useDetailModule(id, isEditMode);
	const { mutate: createModule } = useCreateModule();
	const { mutate: updateModule } = useUpdateModule();

	useEffect(() => {
		if (isEditMode && moduleDetail) {
			reset({
				id: moduleDetail.id,
				name: moduleDetail.name,
				shortName: moduleDetail.shortName,
				remark: moduleDetail.remark,
			});
		}
	}, [moduleDetail, isEditMode, reset]);

	const handleCreateModule = (form: ModuleForm) => {
		createModule(
			{
				name: form.name,
				shortName: form.shortName,
				remark: form.remark,
			},
			{
				onSuccess: () => {
					onClose();
					refetch();
				},
			},
		);
	};
	const handleUpdateModule = (form: ModuleForm) => {
		updateModule(
			{
				id: form.id!,
				name: form.name,
				remark: form.remark || '',
			},
			{
				onSuccess: () => {
					onClose();
					refetch();
				},
			},
		);
	};

	const onSubmit = (form: ModuleForm) => {
		if (isEditMode) {
			handleUpdateModule(form);
		} else {
			handleCreateModule(form);
		}
	};

	return (
		<ModalComponent
			destroyOnClose
			open
			maskClosable={false}
			onCancel={onClose}
			title={isEditMode ? 'Edit Module' : 'Add Module'}
			width={'60%'}
		>
			<Form
				preserve={false}
				labelWrap
				autoComplete='off'
				labelCol={{ span: 6 }}
				wrapperCol={{ span: 18 }}
				style={{ marginTop: 50 }}
				labelAlign='right'
				onFinish={handleSubmit(onSubmit)}
			>
				<Controller
					name='name'
					control={control}
					render={({ field, fieldState: { error } }) => {
						return (
							<Form.Item
								required
								label='Module Name'
								help={error?.message}
								validateStatus={error?.message ? 'error' : ''}
							>
								<Input
									onChange={field.onChange}
									value={field.value}
									placeholder='Please input module name'
								/>
							</Form.Item>
						);
					}}
				/>
				<Controller
					name='shortName'
					control={control}
					render={({ field, fieldState: { error } }) => {
						return (
							<Form.Item
								required
								label='Module Short Name'
								help={error?.message}
								validateStatus={error?.message ? 'error' : ''}
							>
								<Input
									onChange={field.onChange}
									value={field.value}
									disabled={isEditMode}
									placeholder='Please input module short name'
								/>
							</Form.Item>
						);
					}}
				/>
				<Controller
					name='remark'
					control={control}
					render={({ field, fieldState: { error } }) => {
						return (
							<Form.Item
								label='Remark'
								help={error?.message}
								validateStatus={error?.message ? 'error' : ''}
							>
								<Input.TextArea
									onChange={field.onChange}
									value={field.value}
									placeholder='Input a Remark if needed'
								/>
							</Form.Item>
						);
					}}
				/>
				<Flex justify='flex-end' gap='middle'>
					<Button type='default' htmlType='button' onClick={onClose}>
						Cancel
					</Button>
					<Button type='primary' htmlType='submit'>
						Submit
					</Button>
				</Flex>
			</Form>
		</ModalComponent>
	);
};

export default AddModal;
